/* Sessions — now backed by the real Cognito pool.
   ─────────────────────────────────────────────────────────────────────────────
   The `Session` shape is unchanged; only what fills it is. Identity comes from
   the ID token's claims, so `team` and `name` are whatever `pre-token-gen`
   stamped at sign-in (D17) rather than anything this file decides. Ownership is
   the Cognito `sub` (D44).

   There is no session cookie and no backend-for-frontend: tokens live in
   localStorage and the ID token goes to the API in an Authorization header
   (D47). The mechanics are in `tokens.ts`.

   Both ways in are **managed login** — see `signInWithEmail` for why the email
   path cannot be done from our own screen. */

import { useEffect, useState } from 'react';
import { beginSignIn, completeSignIn, currentIdToken, decode, readTokens, writeTokens } from './tokens';
import { AUTH } from './auth-config';


export type Suit = 'Hearts' | 'Spades' | 'Clubs' | 'Diamonds';

export const SUITS: Suit[] = ['Hearts', 'Spades', 'Clubs', 'Diamonds'];
export const SUIT_MARK: Record<Suit, string> = {
  Hearts: '♥', Spades: '♠', Clubs: '♣', Diamonds: '♦',
};
/* Hearts and Diamonds are the red suits, so they take the warm accent; the black
   suits take the olive. Typographic marks, not icons — they carry into print. */
export const SUIT_COLOR: Record<Suit, string> = {
  Hearts: 'var(--color-accent)', Diamonds: 'var(--color-accent)',
  Spades: 'var(--color-accent-2-700)', Clubs: 'var(--color-accent-2-700)',
};

export interface Session {
  userId: string;
  email: string;
  name: string;
  provider: 'google' | 'email' | 'admin';
  team: Suit;
  isAdmin: boolean;
  /** Drives the one-time /welcome screen and nothing else. */
  needsWelcome: boolean;
  signedInAt: number;
}

const KEY = 'mv.session';
/** Which `sub` has already seen /welcome. Local by design — a one-time
 *  nicety does not deserve a round trip or a token claim. */
const WELCOMED_KEY = 'mv.welcomed';

const listeners = new Set<() => void>();
let current: Session | null = read();

function read(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function commit(next: Session | null) {
  current = next;
  if (next) localStorage.setItem(KEY, JSON.stringify(next));
  else localStorage.removeItem(KEY);
  listeners.forEach((fn) => fn());
}

export function getSession(): Session | null { return current; }

export function useSession(): Session | null {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return current;
}

/**
 * Build a session from the ID token's claims. Nothing here is decided locally:
 * `sub`, team and display name all come from Cognito, which is what makes
 * ownership survive a new phone or a cleared browser (D44).
 */
function fromToken(idToken: string): Session | null {
  const c = decode(idToken);
  if (!c) return null;
  const groups = c['cognito:groups'] ?? [];
  const federated = (c.identities ?? []).some((i) => i.providerName === 'Google');
  const session: Session = {
    userId: c.sub,
    email: (c.email ?? '').toLowerCase(),
    name: c['custom:displayName'] || c.name || 'Guest',
    provider: groups.includes('admin') ? 'admin' : federated ? 'google' : 'email',
    team: ((c['custom:team'] as Suit) || 'Hearts'),
    isAdmin: groups.includes('admin'),
    // Local, and deliberately so — /welcome is a one-time nicety, not identity,
    // and does not deserve a round trip or a token claim.
    needsWelcome: localStorage.getItem(WELCOMED_KEY) !== c.sub,
    signedInAt: Date.now(),
  };
  commit(session);
  return session;
}

/**
 * Google, in one tap — `identity_provider=Google` skips Cognito's own provider
 * chooser (D10). This never returns: the page navigates to managed login.
 */
export async function signInWithGoogle(): Promise<never> {
  if (!navigator.onLine) throw new Error('offline');
  return beginSignIn('Google');
}

/**
 * The email path, and it is a redirect rather than our own screen.
 *
 * A guest signing in for the first time does not yet exist in the pool, and
 * Cognito's EMAIL_OTP challenge signs in an *existing* user — the API has no
 * single call that creates a guest and sends them a code. Managed login does
 * both. Calling InitiateAuth ourselves looks like it works, because
 * `preventUserExistenceErrors` returns a challenge for an unknown address on
 * purpose, and then fails at the code with no way to tell a typo from a
 * stranger.
 *
 * That is exactly the silent lockout D46 exists to prevent, so the redirect is
 * the safe shape. See `EmailCode.tsx`, which this leaves unused.
 */
/** Kept for the fallback case: if `/auth/start` is ever unavailable, managed
 *  login can still get a guest in. Not on the normal path. */
export async function signInWithEmailHosted(): Promise<never> {
  if (!navigator.onLine) throw new Error('offline');
  return beginSignIn();
}

/** Called by /auth/callback with `?code=`. */
export async function completeAuthRedirect(code: string): Promise<Session> {
  const tokens = await completeSignIn(code);
  const session = fromToken(tokens.idToken);
  if (!session) throw new Error('bad_token');
  return session;
}

/** The header value for an API call, refreshing silently if needed. Null means
 *  the 30-day refresh token is gone too, and the guest must sign in again. */
export async function authHeader(): Promise<Record<string, string>> {
  const token = await currentIdToken();
  return token ? { Authorization: token } : {};
}

export function updateName(name: string) {
  if (!current) return;
  commit({ ...current, name: name.trim() || current.name });
}

export function completeWelcome() {
  if (!current) return;
  localStorage.setItem(WELCOMED_KEY, current.userId);
  commit({ ...current, needsWelcome: false });
}

/**
 * Restores a session on page load from the stored tokens, refreshing silently
 * if the hour is up. This is what "stays signed in for 30 days" actually is —
 * the refresh token, not the storage mechanism (D47).
 */
export async function restore(): Promise<Session | null> {
  if (!readTokens()) return null;
  const token = await currentIdToken();
  if (!token) { commit(null); return null; }
  return fromToken(token);
}

export function signOut() {
  writeTokens(null);
  commit(null);
}

/**
 * NOT WIRED — deliberately left failing rather than left permissive.
 *
 * It previously accepted any non-empty password, which is the worst possible
 * placeholder for the one credential D40 calls the real thing worth
 * protecting. `admin-web` is SRP-only, and SRP is not something to hand-roll;
 * the options are a client library, enabling USER_PASSWORD_AUTH on that client,
 * or putting the admin through managed login too. That is a decision, not an
 * implementation detail, so it is not being made here.
 */
/**
 * The email path, on our own screen (D46, D10).
 *
 * Two calls, because Cognito has no single one. `/auth/start` makes the user
 * exist — a first-time guest does not, and EMAIL_OTP only signs in someone who
 * does. Then `InitiateAuth` sends the code exactly as the pool is configured
 * to, and `verifyCode` answers the challenge.
 *
 * Managed login could do both on its own page, and that is the version this
 * replaces: it could not be made to look like the rest of the site, and it
 * offered a second "Continue with Google" button next to the one the guest had
 * already walked past.
 */
export interface CodeRequest { email: string; sentAt: number }

export class CodeError extends Error {
  constructor(public kind: 'invalid' | 'expired' | 'offline' | 'rate_limited') { super(kind); }
}

/**
 * Cognito's two failure modes here mean different things to a guest, and
 * collapsing them is what makes a mistyped digit look like a dead end.
 * Measured against the live pool, 26 Aug 2026:
 *
 *   CodeMismatchException  — wrong digits, session still good. Retry works.
 *   NotAuthorizedException — the challenge is spent. Retrying is hopeless even
 *                            with the right code; only a new code helps.
 *
 * The session survives the first couple of wrong answers and then starts
 * returning NotAuthorized, with no clean cut-off — so this reads the error
 * rather than counting attempts.
 */
function codeErrorFrom(err: unknown): CodeError {
  const name = (err as { __type?: string; name?: string })?.__type
    ?? (err as { name?: string })?.name ?? '';
  return new CodeError(name.includes('CodeMismatch') ? 'invalid' : 'expired');
}

const PENDING_KEY = 'mv.pendingEmail';
const cognitoIdp = `https://cognito-idp.${AUTH.region}.amazonaws.com/`;

async function idp(target: string, body: unknown): Promise<Record<string, unknown>> {
  const res = await fetch(cognitoIdp, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw json;
  return json;
}

export async function requestCode(email: string): Promise<CodeRequest> {
  if (!navigator.onLine) throw new CodeError('offline');
  const address = email.trim().toLowerCase();

  const started = await fetch(`${AUTH.api}/auth/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: address }),
  });
  if (started.status === 429) throw new CodeError('rate_limited');
  if (!started.ok) throw new CodeError('invalid');

  const res = await idp('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: AUTH.guestClientId,
    AuthParameters: { USERNAME: address, PREFERRED_CHALLENGE: 'EMAIL_OTP' },
  });

  const pending: CodeRequest = { email: address, sentAt: Date.now() };
  // The Cognito challenge session is what ties the code to this request. It is
  // single-use and short-lived, so sessionStorage rather than localStorage.
  sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ...pending, session: res.Session }));
  return pending;
}

export function getPendingCodeRequest(): CodeRequest | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as CodeRequest) : null;
  } catch {
    return null;
  }
}

export function clearPendingCodeRequest() { sessionStorage.removeItem(PENDING_KEY); }

export async function verifyCode(code: string): Promise<Session> {
  if (!navigator.onLine) throw new CodeError('offline');
  const raw = sessionStorage.getItem(PENDING_KEY);
  if (!raw) throw new CodeError('expired');
  const pending = JSON.parse(raw) as CodeRequest & { session: string };

  let res: Record<string, unknown>;
  try {
    res = await idp('RespondToAuthChallenge', {
      ChallengeName: 'EMAIL_OTP',
      ClientId: AUTH.guestClientId,
      Session: pending.session,
      ChallengeResponses: { USERNAME: pending.email, EMAIL_OTP_CODE: code },
    });
  } catch (err) {
    const failure = codeErrorFrom(err);
    // A spent challenge cannot be retried, so drop it. That makes "Send another
    // code" the only remaining action, which is exactly the right one — rather
    // than leaving a guest retyping a correct code into a dead session.
    if (failure.kind === 'expired') clearPendingCodeRequest();
    throw failure;
  }

  const auth = res.AuthenticationResult as
    | { IdToken: string; AccessToken: string; RefreshToken: string; ExpiresIn: number }
    | undefined;
  if (!auth) throw new CodeError('invalid');

  writeTokens({
    idToken: auth.IdToken,
    accessToken: auth.AccessToken,
    refreshToken: auth.RefreshToken,
    expiresAt: Date.now() + auth.ExpiresIn * 1000,
  });
  clearPendingCodeRequest();
  const session = fromToken(auth.IdToken);
  if (!session) throw new CodeError('invalid');
  return session;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}
